import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import useSeo from "@/lib/useSeo";

const LIKERT = [
  { v: 1, label: "Strongly Disagree" },
  { v: 2, label: "Disagree" },
  { v: 3, label: "Neutral" },
  { v: 4, label: "Agree" },
  { v: 5, label: "Strongly Agree" },
];

export default function Diagnostic() {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(-1); // -1 = intro
  const [participantName, setParticipantName] = useState("");
  const [participantRole, setParticipantRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [params] = useSearchParams();
  const boardCode = params.get("board") || "";
  const memberToken = params.get("member") || "";
  const teamToken = params.get("team") || "";
  const [sessionInfo, setSessionInfo] = useState(null);
  const [contextInfo, setContextInfo] = useState(null); // board member or team context
  const navigate = useNavigate();

  useSeo({
    title: "Take the Value Realisation Index — 20-Minute Board Diagnostic",
    description: "Twenty-five questions across five dimensions. In twenty minutes, discover where your board is losing value — and receive a personalised Value Realisation Index score.",
    path: "/diagnostic",
  });

  useEffect(() => {
    api.get("/questions").then(({ data }) => setQuestions(data.questions));
    if (boardCode) {
      api.get(`/board-sessions/${boardCode}`).then(({ data }) => setSessionInfo(data)).catch(() => {});
    }
    if (memberToken) {
      api.get(`/board-members/by-own-token/${memberToken}`).then(({ data }) => {
        setContextInfo({ kind: "member", ...data });
        setParticipantName((prev) => prev || data.name || "");
        setParticipantRole((prev) => prev || data.role || "");
      }).catch(() => {});
    } else if (teamToken) {
      api.get(`/board-members/by-team-token/${teamToken}`).then(({ data }) => {
        setContextInfo({ kind: "team", ...data });
      }).catch(() => {});
    }
  }, [boardCode, memberToken, teamToken]);

  const total = questions.length;
  const q = idx >= 0 && idx < total ? questions[idx] : null;
  const answered = Object.keys(answers).length;
  const progress = total ? Math.round((answered / total) * 100) : 0;

  const setAnswer = (v) => {
    setAnswers({ ...answers, [q.id]: v });
    // auto-advance
    setTimeout(() => {
      if (idx < total - 1) setIdx(idx + 1);
    }, 220);
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        answers,
        participant_name: participantName || null,
        participant_role: participantRole || null,
        board_session_code: boardCode || null,
        member_token: memberToken || null,
        team_token: teamToken || null,
      };
      const { data } = await api.post("/submissions", payload);
      navigate(`/results/${data.id}`);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-linen">
      <Navbar />

      {/* Progress line */}
      <div className="sticky top-0 z-10 bg-linen border-b border-oxford/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <div className="tick-label">
            The Value Realisation Index
          </div>
          <div className="tick-label" data-testid="progress-label">
            {idx >= 0 ? `${Math.min(idx + 1, total)} / ${total}` : "Intro"}
          </div>
        </div>
        <div className="h-px bg-oxford/10">
          <motion.div
            className="h-px bg-oxford"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <AnimatePresence mode="wait">
          {idx === -1 && (
            <motion.div key="intro" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
              <div className="tick-label mb-8">A twenty-minute diagnostic</div>
              <h1 className="display-serif text-5xl md:text-7xl">
                Where is value leaking from your organisation?
              </h1>
              <div className="rule my-12 max-w-md" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-10">
                <div>
                  <p className="text-lg text-charcoal leading-relaxed">
                    Twenty-five questions across five dimensions. Rate each
                    from 1 (Strongly Disagree) to 5 (Strongly Agree). Answer
                    honestly and quickly — first-instinct answers are the most
                    accurate.
                  </p>
                  <p className="text-lg text-charcoal leading-relaxed mt-6">
                    You will receive a Value Realisation Index score
                    between 0 and 125, a spider chart of the five dimensions,
                    and a tailored interpretation.
                  </p>
                </div>
                <div className="border border-oxford/15 bg-linen p-10">
                  <div className="tick-label mb-6">Your details (optional)</div>
                  <label className="tick-label block mb-2">Your name</label>
                  <input
                    data-testid="intro-participant-name"
                    value={participantName}
                    onChange={(e) => setParticipantName(e.target.value)}
                    placeholder="Optional"
                    className="w-full border border-oxford/20 px-3 py-2 mb-4 bg-parchment focus:outline-none focus:border-oxford"
                  />
                  <label className="tick-label block mb-2">Board role</label>
                  <input
                    data-testid="intro-participant-role"
                    value={participantRole}
                    onChange={(e) => setParticipantRole(e.target.value)}
                    placeholder="e.g. Chair, CEO, NED"
                    className="w-full border border-oxford/20 px-3 py-2 bg-parchment focus:outline-none focus:border-oxford"
                  />
                  {sessionInfo && (
                    <div className="mt-6 p-4 bg-parchment border border-oxford/10">
                      <div className="tick-label mb-2">Board Session</div>
                      <div className="text-sm">{sessionInfo.organisation} · <span className="font-mono text-xs">{sessionInfo.code}</span></div>
                      <div className="text-xs text-charcoal mt-1">Facilitated by {sessionInfo.facilitator_name}</div>
                    </div>
                  )}
                  {contextInfo?.kind === "member" && (
                    <div className="mt-6 p-4 bg-parchment border border-orange/50" data-testid="context-member">
                      <div className="tick-label-accent mb-1">You are taking this as</div>
                      <div className="display-serif text-xl">{contextInfo.name}</div>
                      <div className="text-xs text-charcoal mt-1">{contextInfo.role || "Board director"} · {contextInfo.organisation}</div>
                    </div>
                  )}
                  {contextInfo?.kind === "team" && (
                    <div className="mt-6 p-4 bg-parchment border border-orange/50" data-testid="context-team">
                      <div className="tick-label-accent mb-1">You are a direct report of</div>
                      <div className="display-serif text-xl">{contextInfo.board_member_name}</div>
                      <div className="text-xs text-charcoal mt-1">{contextInfo.organisation}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-16">
                <button data-testid="intro-begin-btn" onClick={() => setIdx(0)} className="btn-oxford">
                  Begin <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </motion.div>
          )}

          {q && (
            <motion.div key={q.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}>
              <div className="flex items-baseline justify-between mb-10">
                <div className="tick-label">
                  Dimension {["Clarity","Alignment","Execution","Value Realisation","Choice"].indexOf(q.dimension) + 1} / 5 · {q.dimension}
                </div>
                <div className="tick-label">Question {q.id} of {total}</div>
              </div>

              <h2 data-testid={`question-text-${q.id}`} className="display-serif text-3xl md:text-5xl leading-tight">
                {q.text}
              </h2>

              <div className="mt-16 grid grid-cols-1 md:grid-cols-5 gap-3">
                {LIKERT.map((opt) => {
                  const active = answers[q.id] === opt.v;
                  return (
                    <button
                      key={opt.v}
                      data-testid={`answer-q${q.id}-v${opt.v}`}
                      onClick={() => setAnswer(opt.v)}
                      className={`border p-6 text-left transition-all ${
                        active
                          ? "border-oxford bg-oxford text-linen"
                          : "border-oxford/20 bg-linen hover:border-oxford hover:-translate-y-0.5"
                      }`}
                    >
                      <div className={`font-mono text-2xl ${active ? "text-linen" : "text-oxford"}`}>{opt.v}</div>
                      <div className={`mt-3 text-sm ${active ? "text-linen/80" : "text-charcoal"}`}>{opt.label}</div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-16 flex items-center justify-between">
                <button
                  data-testid="q-prev-btn"
                  onClick={() => setIdx(Math.max(-1, idx - 1))}
                  className="btn-ghost"
                >
                  <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back
                </button>
                {idx === total - 1 && answered === total ? (
                  <button
                    data-testid="q-submit-btn"
                    onClick={submit}
                    disabled={submitting}
                    className="btn-oxford"
                  >
                    {submitting ? "Calculating…" : "Reveal my VRI"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                ) : (
                  <button
                    data-testid="q-next-btn"
                    onClick={() => setIdx(Math.min(total - 1, idx + 1))}
                    className="btn-ghost"
                    disabled={!answers[q.id]}
                    style={{ opacity: answers[q.id] ? 1 : 0.4 }}
                  >
                    Next <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                )}
              </div>

              {error && <div className="mt-6 text-heritage text-sm" data-testid="q-error">{error}</div>}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
